import { useEffect, useState } from "react";
import {
  Divider,
  Skeleton,
  Table as MuiTable,
  TableContainer,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "../../../../components";
import {
  OrdemServicoService,
  OrdemServicosStatusPersonalizadosService,
} from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";
import { format, parseISO } from "date-fns";
import { ListPesquisaSatisfacaoOS } from "../../../ordem-servicos/add-ordem-servico/components";

const ordemServicoService = new OrdemServicoService();
const statusPersonalizadosService =
  new OrdemServicosStatusPersonalizadosService();

const COR_ABERTO = "#4FBB53";
const COR_FECHADO = "#004780";
const COR_CANCELADO = "#DC3545";
const COR_AVALIACAO = "#164221";

function formatarNome(nome) {
  if (!nome) return nome;
  return nome
    .toLowerCase()
    .split(" ")
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(" ");
}

function formatarData(data) {
  if (!data) return "-";
  return format(parseISO(data.slice(0, 10)), "dd/MM/yyyy");
}

function statusBase() {
  return [
    { value: "ABERTO", label: "Aberto", cor: COR_ABERTO },
    { value: "FECHADO", label: "Fechado", cor: COR_FECHADO },
    { value: "CANCELADO", label: "Cancelado", cor: COR_CANCELADO },
  ];
}

function MonitorOsListagemStatus({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusList, setStatusList] = useState(statusBase());
  const [statusSel, setStatusSel] = useState([]);
  const [hasPesquisas, setHasPesquisas] = useState(false);

  useEffect(() => {
    buscarStatus();
  }, []);

  useEffect(() => {
    buscarListagem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody, statusSel, hasPesquisas]);

  async function buscarStatus() {
    const result = await statusPersonalizadosService.getAll();
    if (!result.isAxiosError) {
      const personalizados = (result.data || [])
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        .map((s) => ({
          value: String(s.id),
          label: formatarNome(s.descricao),
          cor: s.cor || "#1976d2",
        }));
      setStatusList([
        { value: "ABERTO", label: "Aberto", cor: COR_ABERTO },
        ...personalizados,
        { value: "FECHADO", label: "Fechado", cor: COR_FECHADO },
        { value: "CANCELADO", label: "Cancelado", cor: COR_CANCELADO },
      ]);
    } else {
      responseErros(result);
    }
  }

  async function buscarListagem() {
    setLoading(true);
    const statusEnvio = statusSel.map((v) =>
      v === "ABERTO" || v === "FECHADO" || v === "CANCELADO" ? v : Number(v),
    );
    const body = {
      ...filtrosBody,
      filtros: {
        ...(filtrosBody?.filtros || {}),
        ...(statusEnvio.length ? { status: statusEnvio } : {}),
        ...(hasPesquisas ? { hasPesquisas: true } : {}),
      },
    };
    const result =
      await ordemServicoService.getRelatorioDashboardListagemStatus(body);
    if (!result.isAxiosError) {
      setRows(result.data || []);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  function toggleStatus(value) {
    setStatusSel((prev) => {
      const selecionado = prev.includes(value);
      if (selecionado && value === "FECHADO") setHasPesquisas(false);
      return selecionado ? prev.filter((v) => v !== value) : [...prev, value];
    });
  }

  function statusInfo(row) {
    if (row.statusPersonalizadoId) {
      const encontrado = statusList.find(
        (s) => s.value === String(row.statusPersonalizadoId),
      );
      return {
        label: encontrado?.label || "Personalizado",
        cor: encontrado?.cor || "#1976d2",
      };
    }
    const base = statusList.find((s) => s.value === row.status);
    return { label: base?.label || row.status, cor: base?.cor || "#94a3b8" };
  }

  const ativoHeader = rows.find((r) => r.ativoLabel)?.ativoLabel || "Ativo";
  const fechadoSelecionado = statusSel.includes("FECHADO");
  const canceladoUnico = statusSel.length === 1 && statusSel[0] === "CANCELADO";

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Listagem de Ordens de Serviço
          </Typography>
          <i
            className="ph-bold ph-list-checks"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {statusList.map((st) => {
            const isSel = statusSel.includes(st.value);
            return (
              <Chip
                key={st.value}
                size="small"
                clickable
                icon={<i className="ph-fill ph-circle" />}
                label={st.label}
                onClick={() => toggleStatus(st.value)}
                sx={{
                  fontWeight: isSel ? 700 : 500,
                  color: isSel ? "#fff" : "#334155",
                  backgroundColor: isSel ? st.cor : alpha(st.cor, 0.1),
                  "& .MuiChip-icon": {
                    fontSize: 11,
                    color: isSel ? "#fff" : st.cor,
                  },
                  "&:hover": {
                    backgroundColor: isSel ? st.cor : alpha(st.cor, 0.22),
                  },
                }}
              />
            );
          })}
          {fechadoSelecionado && (
            <Chip
              size="small"
              clickable
              icon={<i className="ph-bold ph-smiley" />}
              label="Com Avaliação Realizada"
              onClick={() => setHasPesquisas((prev) => !prev)}
              sx={{
                fontWeight: hasPesquisas ? 700 : 500,
                color: hasPesquisas ? "#fff" : "#334155",
                backgroundColor: hasPesquisas
                  ? COR_AVALIACAO
                  : alpha(COR_AVALIACAO, 0.1),
                "& .MuiChip-icon": {
                  fontSize: 13,
                  color: hasPesquisas ? "#fff" : COR_AVALIACAO,
                },
                "&:hover": {
                  backgroundColor: hasPesquisas
                    ? COR_AVALIACAO
                    : alpha(COR_AVALIACAO, 0.22),
                },
              }}
            />
          )}
        </Box>
        {loading ? (
          <Box>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={40}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            gap={1}
          >
            <i
              className="ph-bold ph-tray"
              style={{ fontSize: 32, color: "#94a3b8" }}
            />
            <Typography variant="body2" color="text.secondary">
              Nenhuma O.S encontrada no período selecionado.
            </Typography>
          </Box>
        ) : (
          <TableContainer style={{ maxHeight: 420 }}>
            <MuiTable size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Nº O.S
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Cliente
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    {ativoHeader}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Abertura
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Previsão
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                  >
                    Valor
                  </TableCell>
                  {!canceladoUnico && (
                    <TableCell
                      sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                    >
                      Prazo
                    </TableCell>
                  )}
                  {fechadoSelecionado && (
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 700, backgroundColor: "#f8fafc" }}
                    >
                      Satisfação
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const info = statusInfo(row);
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor: row.atrasado ? "#fff8f8" : "inherit",
                        "&:hover": {
                          backgroundColor: row.atrasado ? "#fff0f0" : "#f8fafc",
                        },
                      }}
                    >
                      <TableCell align="center">
                        <Tooltip title={info.label} arrow placement="right">
                          <i
                            className="ph-fill ph-circle"
                            style={{ color: info.cor, fontSize: 16 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.serie}-{row.numero}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.cliente}
                      >
                        {row.cliente}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.ativo}
                      >
                        {row.ativo || "-"}
                      </TableCell>
                      <TableCell align="center">
                        {formatarData(row.dataAbertura)}
                      </TableCell>
                      <TableCell align="center">
                        {formatarData(row.dataPrevista)}
                      </TableCell>
                      <TableCell align="right">
                        {formatPrice(row.valor)}
                      </TableCell>
                      {!canceladoUnico && (
                        <TableCell align="center">
                          {row.prazoLabel ? (
                            <Chip
                              label={row.prazoLabel}
                              size="small"
                              color={
                                row.atrasado
                                  ? "error"
                                  : row.prazoLabel === "Vence hoje"
                                  ? "warning"
                                  : "success"
                              }
                              variant={row.atrasado ? "filled" : "outlined"}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      )}
                      {fechadoSelecionado && (
                        <TableCell align="center">
                          {row.pesquisas?.length > 0 ? (
                            <ListPesquisaSatisfacaoOS
                              listaOrdemServicoPesquisas={row.pesquisas}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsListagemStatus;
